import React, { useState } from "react";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./components/AuthLayout";
import { AuthService } from "../../services/auth.service";
import { setAuthSession, updateAuthUser } from "./components/authStore";
import AuthPopup from "./components/AuthPopup";

function PasswordField({ label, value, onChange, show, onToggle }) {
  const inputBase =
    "w-full h-[46px] rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[14px] text-slate-700 placeholder-slate-400 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]";

  return (
    <div className="space-y-2">
      <label className="text-[12px] font-semibold tracking-wider text-slate-500">
        {label}
      </label>

      <div className="relative">
        <input
          className={inputBase + " pr-12"}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
          autoComplete="new-password"
        />

        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
}

function routeFromNextAction(nextAction) {
  switch (String(nextAction || "").toUpperCase()) {
    case "ONBOARDING":
    case "ONBOARDING_REQUIRED":
    case "ONBOARDING_REJECTED":
    case "REJECTED":
      return "/admin/onboarding";

    case "WAITING_APPROVAL":
    case "WAITING_FOR_ADMIN_APPROVAL":
      return "/admin/waiting-approval";

    default:
      return "/admin/dashboard";
  }
}

export default function ChangeTemporaryPassword() {
  const nav = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [popup, setPopup] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onClose: null,
  });

  const showPopup = ({ type = "error", title = "Alert", message, onClose = null }) => {
    setPopup({ open: true, type, title, message, onClose });
  };

  const closePopup = () => {
    const cb = popup.onClose;
    setPopup((p) => ({ ...p, open: false, onClose: null }));
    cb?.();
  };

  const buttonBase =
    "w-full h-[46px] rounded-[10px] bg-[#d86a78] text-white font-semibold shadow-[0_12px_26px_rgba(216,106,120,0.22)] hover:bg-[#c95b69] disabled:opacity-60";

  const onSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!newPassword || !confirmPassword) {
        return showPopup({
          type: "error",
          title: "Missing Password",
          message: "Please fill all password fields.",
        });
      }

      if (newPassword !== confirmPassword) {

        return showPopup({
          type: "error",
          title: "Password Mismatch",
          message: "New password and confirm password must match",
        });
      }

      setSaving(true);

      const res = await AuthService.changeTemporaryPassword({
        newPassword,
        confirmPassword,
      });

      updateAuthUser({
        mustChangePassword: false,
        ...(res?.admin || {}),
      });

      setAuthSession({
        admin: res?.admin,
        dashboardAccess: res?.dashboardAccess,
        warehouseAccess: res?.warehouseAccess,
      });

      nav(routeFromNextAction(res?.nextAction), { replace: true });
    } catch (err) {
      showPopup({
        type: "error",
        title: "Change Temporary Password Failed",
        message: err?.message || "Unable to change temporary password",
      });

    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout>
      <div className="mx-auto w-full max-w-[420px] rounded-[24px] border border-[#f6d8dd] bg-white p-6 shadow-[0_20px_70px_rgba(216,106,120,0.12)] sm:p-8">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#fff3f5] text-[#d86a78]">
          <ShieldCheck size={28} />
        </div>

        <h1 className="text-center text-[30px] font-bold tracking-wide text-[#d86a78]">
          CHANGE TEMP PASSWORD
        </h1>

        <p className="mt-3 text-center text-[13px] leading-5 text-slate-500">
          First time login detected. Set your new password to continue.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-5">
          <PasswordField
            label="NEW PASSWORD"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
          />

          <PasswordField
            label="CONFIRM PASSWORD"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showConfirm}
            onToggle={() => setShowConfirm((v) => !v)}
          />

          <button type="submit" className={buttonBase} disabled={saving}>
            {saving ? "PLEASE WAIT..." : "UPDATE PASSWORD"}
          </button>
        </form>
      </div>
      <AuthPopup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </AuthLayout>
  );
}